import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  LucideAngularModule,
  Calendar,
  Users,
  Globe,
  FileText,
  Check,
  Share,
  Image,
  ArrowLeft,
  BookOpen,
} from 'lucide-angular';
import { CountryService } from 'src/app/trip-calculator/services/country.service';
import { Trip } from 'src/app/trip-calculator/models/trip';
import { Country } from 'src/app/trip-calculator/models/country';
import { PdfComponent } from 'src/app/shared/pdf/pdf.component';
import type { ChartLogoSource } from '../../models';
import { TooltipModule } from 'primeng/tooltip';
import { BrochureDownloadService } from 'src/app/shared/services/brochure-download.service';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { MethodologyService } from 'src/app/shared/services/methodology.service';

@Component({
  selector: 'app-trip-impact-header',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    TranslocoModule,
    TooltipModule,
    PdfComponent,
  ],
  templateUrl: './trip-impact-header.component.html',
  styles: [],
})
export class TripImpactHeaderComponent {
  @Input() trip!: Trip;
  @Input() countries: Country[] = [];
  @Input() hasContributed: boolean = false;
  @Input() clients: Array<{ id: string; name: string }> = [];
  /** Logos proposés dans la modale d'export PDF (company du trip + clients). */
  @Input() logoSources: ChartLogoSource[] = [];
  countryService = inject(CountryService);
  brochureDownloadService = inject(BrochureDownloadService);
  packLimitationService = inject(PackLimitationsService)
  methodologyService = inject(MethodologyService);

  private transloco = inject(TranslocoService);

  constructor(private router: Router) {}

  // Icons
  calendarIcon = Calendar;
  usersIcon = Users;
  globeIcon = Globe;
  fileTextIcon = FileText;
  checkIcon = Check;
  shareIcon = Share;
  imageIcon = Image;
  arrowLeftIcon = ArrowLeft;
  bookIcon = BookOpen;

  getCountriesText(): string {
    if (!this.countries || this.countries.length === 0) return '';

    if (this.countries.length === 1) {
      return this.countryService.getCountryLabelByLang(this.countries[0]);
    }

    if (this.countries.length <= 3) {
      return this.countries
        .map((country) => this.countryService.getCountryLabelByLang(country))
        .join(', ');
    }

    return `${this.countries.length} ${this.transloco.translate('common.countries')}`;
  }

  getClientsText(): string {
    if (this.clients.length === 0) return '';
    if (this.clients.length === 1) return this.clients[0].name;
    if (this.clients.length <= 3) {
      return this.clients.map((c) => c.name).join(', ');
    }
    return `${this.clients[0].name} et ${this.clients.length - 1} autres`;
  }

  get hasBrochures(): boolean {
    return this.brochureDownloadService.hasBrochures(this.trip);
  }

  get brochures() {
    return this.brochureDownloadService.getBrochures(this.trip);
  }

  getBrochureTooltip(filename: string): string {
    const baseTooltip = this.transloco.translate('dashboard.brochureTooltip');
    return `${filename}\n\n${baseTooltip}`;
  }

  exportPdf() {
    // TODO: Implémenter l'export PDF
    console.log('Export PDF pour le voyage:', this.trip.id);
  }

  backToCalculator() {
    if (this.trip.id) {
      this.router.navigate(['/home/holiday-calculator'], {
        queryParams: { tripId: this.trip.id },
      });
    }
  }
}
